import { type FC, useMemo } from 'react';
import type { DisplayType } from '../types';
import {
  HistoricalYearsTooltip,
  type SalesSummaryProperties,
  SummaryHeader,
  SummaryTile,
  useImportMonth,
} from './index';

export const AverageOrderValueSummary: FC<SalesSummaryProperties> = ({
  lastImported,
  thisYearSales,
  thisYearEstimates,
  historicalYears,
}) => {
  const displayType: DisplayType = 'Average Order Value';
  const thisYear = useMemo(() => thisYearSales.year, [thisYearSales]);
  const lastYear = useMemo(() => thisYear - 1, [thisYear]);
  const lastYearSales = useMemo(() => historicalYears[0], [historicalYears]);
  const { index, monthName, isMtdCurrent } = useImportMonth(lastImported);

  const lastYearAverageMonthTicket = useMemo(() => {
    if (!lastYearSales || lastYearSales.totals_to_date_month.transactions === 0) {
      return undefined;
    }

    return (
      lastYearSales.totals_to_date_month.sales / lastYearSales.totals_to_date_month.transactions
    );
  }, [lastYearSales]);
  const averageMonthTicket = useMemo(() => {
    if (thisYearSales.totals_to_date_month.transactions === 0) {
      return undefined;
    }

    return (
      thisYearSales.totals_to_date_month.sales / thisYearSales.totals_to_date_month.transactions
    );
  }, [thisYearSales]);

  const lastYearAverageTicket = useMemo(() => {
    if (!lastYearSales || lastYearSales.totals_to_date.transactions === 0) {
      return undefined;
    }

    return lastYearSales.totals_to_date.sales / lastYearSales.totals_to_date.transactions;
  }, [lastYearSales]);
  const yearAverageTicket = useMemo(() => {
    if (thisYearSales.totals_to_date.transactions === 0) {
      return undefined;
    }

    return thisYearSales.totals_to_date.sales / thisYearSales.totals_to_date.transactions;
  }, [thisYearSales]);

  const historicalTooltipContent = useMemo(() => {
    if (historicalYears.length === 0) {
      return undefined;
    }

    return <HistoricalYearsTooltip historicalYears={historicalYears} displayType={displayType} />;
  }, [historicalYears]);

  return (
    <div className="grid grid-flow-row">
      <SummaryHeader>{monthName}</SummaryHeader>
      <div className="grid grid-flow-col">
        <SummaryTile
          title={`${lastYear} MTD`}
          current={lastYearAverageMonthTicket}
          timespan="lastYear"
          format="currency"
        />
        <SummaryTile
          title={`${thisYearSales.year} MTD`}
          current={isMtdCurrent ? averageMonthTicket : undefined}
          previous={isMtdCurrent ? lastYearAverageMonthTicket : undefined}
          timespan="thisYear"
          format="currency"
        />
        <SummaryTile
          title={`${thisYearSales.year} Total ~`}
          current={
            thisYearEstimates.monthly[index].transactions === 0
              ? undefined
              : thisYearEstimates.monthly[index].sales /
                thisYearEstimates.monthly[index].transactions
          }
          timespan="thisYearEstimates"
          format="currency"
        />
      </div>
      <SummaryHeader>Year</SummaryHeader>
      <div className="grid grid-flow-col">
        <SummaryTile
          title={`${lastYear} YTD`}
          current={lastYearAverageTicket}
          timespan="lastYear"
          format="currency"
        />
        <SummaryTile
          title={`${thisYearSales.year} YTD`}
          current={yearAverageTicket}
          previous={lastYearAverageTicket}
          timespan="thisYear"
          format="currency"
          tooltipContent={historicalTooltipContent}
        />
        <SummaryTile
          title={`${thisYearSales.year} Total ~`}
          current={
            thisYearEstimates.totals.transactions === 0
              ? undefined
              : thisYearEstimates.totals.sales / thisYearEstimates.totals.transactions
          }
          timespan="thisYearEstimates"
          format="currency"
        />
      </div>
    </div>
  );
};
